<template>
    <div class="tw-w-full tw-grid tw-grid-cols-12 tw-gap-6">
        <div class="tw-col-span-12 lg:tw-col-start-3 lg:tw-col-end-11 xl:tw-col-start-4 xl:tw-col-end-10 tw-space-y-5">

            <!-- ── Avatar + name header ── -->
            <div class="tw-bg-white lg:tw-rounded-xl lg:tw-border tw-border-y tw-border-solid tw-border-gray-200 tw-p-6">
                <div class="tw-flex tw-items-center tw-gap-5">

                    <!-- Avatar (click to pick a new image) -->
                    <div
                        class="tw-relative tw-flex-shrink-0 tw-w-[72px] tw-h-[72px] tw-rounded-full tw-overflow-hidden tw-cursor-pointer tw-group tw-border-2 tw-border-solid tw-border-primary-300"
                        @click="pickImage"
                        title="Change profile photo"
                    >
                        <img :src="avatarSrc" alt="avatar" class="tw-w-full tw-h-full tw-object-cover" />
                        <div class="tw-absolute tw-inset-0 tw-bg-black/40 tw-flex tw-items-center tw-justify-center tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity">
                            <icon icon="solar:camera-bold" class="tw-text-white tw-text-xl" />
                        </div>
                        <input ref="fileRef" type="file" accept="image/*" class="tw-hidden" @change="onFile" />
                    </div>

                    <div class="tw-min-w-0">
                        <p class="tw-font-bold tw-text-gray-900 tw-text-lg tw-truncate">{{ form.name || '—' }}</p>
                        <p class="tw-text-sm tw-text-gray-400 tw-truncate">{{ form.email }}</p>
                        <button class="tw-mt-1 tw-text-xs tw-text-primary-500 hover:tw-text-primary-700 tw-transition-colors" @click="pickImage">
                            Change photo
                        </button>
                    </div>
                </div>
            </div>

            <!-- ── Basic info ── -->
            <div class="tw-bg-white lg:tw-rounded-xl lg:tw-border tw-border-y tw-border-solid tw-border-gray-200">
                <div class="tw-px-6 tw-pt-5 tw-pb-4 tw-border-b tw-border-solid tw-border-gray-100">
                    <p class="tw-font-semibold tw-text-gray-800">Personal Information</p>
                </div>
                <div class="tw-p-6 tw-space-y-2">

                    <!-- Name -->
                    <div>
                        <label class="tw-block tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-1">Full Name</label>
                        <input
                            v-model="form.name"
                            type="text"
                            placeholder="Your name"
                            class="tw-w-full tw-rounded-lg tw-border tw-border-solid tw-px-3 tw-py-2.5 tw-text-sm tw-text-gray-900 tw-outline-none tw-transition-colors"
                            :class="fieldErr('name') ? 'tw-border-red-400 focus:tw-border-red-500' : 'tw-border-gray-300 focus:tw-border-primary-500'"
                        />
                        <p v-if="fieldErr('name')" class="tw-mt-1 tw-text-xs tw-text-red-500">{{ fieldErr('name') }}</p>
                    </div>

                    <!-- Email -->
                    <div>
                        <label class="tw-block tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-1">Email Address</label>
                        <input
                            v-model="form.email"
                            type="email"
                            placeholder="your@email.com"
                            class="tw-w-full tw-rounded-lg tw-border tw-border-solid tw-px-3 tw-py-2.5 tw-text-sm tw-text-gray-900 tw-outline-none tw-transition-colors"
                            :class="fieldErr('email') ? 'tw-border-red-400 focus:tw-border-red-500' : 'tw-border-gray-300 focus:tw-border-primary-500'"
                        />
                        <p v-if="fieldErr('email')" class="tw-mt-1 tw-text-xs tw-text-red-500">{{ fieldErr('email') }}</p>
                    </div>

                </div>
            </div>

            <!-- ── Password (collapsible) ── -->
            <div class="tw-bg-white lg:tw-rounded-xl lg:tw-border tw-border-y tw-border-solid tw-border-gray-200">
                <button
                    type="button"
                    class="tw-w-full tw-flex tw-items-center tw-justify-between tw-px-6 tw-py-4 tw-text-left"
                    @click="showPwd = !showPwd"
                >
                    <div class="tw-flex tw-items-center tw-gap-3">
                        <icon icon="solar:lock-keyhole-bold-duotone" class="tw-text-gray-400 tw-text-lg" />
                        <span class="tw-font-semibold tw-text-gray-800">Change Password</span>
                    </div>
                    <icon
                        :icon="showPwd ? 'solar:alt-arrow-up-bold' : 'solar:alt-arrow-down-bold'"
                        class="tw-text-gray-400"
                    />
                </button>

                <div v-show="showPwd" class="tw-px-6 tw-pb-6 tw-border-t tw-border-solid tw-border-gray-100 tw-pt-5 tw-space-y-2">

                    <div>
                        <label class="tw-block tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-1">New Password</label>
                        <input
                            v-model="form.password"
                            type="password"
                            placeholder="Leave empty to keep current"
                            class="tw-w-full tw-rounded-lg tw-border tw-border-solid tw-px-3 tw-py-2.5 tw-text-sm tw-text-gray-900 tw-outline-none tw-transition-colors"
                            :class="fieldErr('password') ? 'tw-border-red-400 focus:tw-border-red-500' : 'tw-border-gray-300 focus:tw-border-primary-500'"
                        />
                        <p v-if="fieldErr('password')" class="tw-mt-1 tw-text-xs tw-text-red-500">{{ fieldErr('password') }}</p>
                    </div>

                    <div>
                        <label class="tw-block tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-1">Confirm New Password</label>
                        <input
                            v-model="form.password_confirmation"
                            type="password"
                            placeholder="Repeat new password"
                            class="tw-w-full tw-rounded-lg tw-border tw-border-solid tw-px-3 tw-py-2.5 tw-text-sm tw-text-gray-900 tw-outline-none tw-transition-colors"
                            :class="fieldErr('password_confirmation') ? 'tw-border-red-400 focus:tw-border-red-500' : 'tw-border-gray-300 focus:tw-border-primary-500'"
                        />
                        <p v-if="fieldErr('password_confirmation')" class="tw-mt-1 tw-text-xs tw-text-red-500">{{ fieldErr('password_confirmation') }}</p>
                    </div>

                </div>
            </div>

            <!-- ── Feedback + save ── -->
            <div class="tw-flex tw-flex-col tw-gap-3">

                <div v-if="success" class="tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-3 tw-rounded-lg tw-bg-green-50 tw-border tw-border-solid tw-border-green-200">
                    <icon icon="solar:check-circle-bold-duotone" class="tw-text-green-500 tw-text-xl tw-flex-shrink-0" />
                    <span class="tw-text-sm tw-text-green-700">{{ success }}</span>
                </div>

                <div v-if="failure" class="tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-3 tw-rounded-lg tw-bg-red-50 tw-border tw-border-solid tw-border-red-200">
                    <icon icon="solar:danger-triangle-bold-duotone" class="tw-text-red-500 tw-text-xl tw-flex-shrink-0" />
                    <span class="tw-text-sm tw-text-red-700">{{ failure }}</span>
                </div>

                <div class="tw-flex tw-justify-end">
                    <a-button :loading="saving" :disabled="saving" @click="save" class="tw-min-w-[140px]">
                        Save Changes
                    </a-button>
                </div>

            </div>

        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useAuthStore } from '@/stores'
import { updateProfile } from '@/api/me'

const authStore = useAuthStore()

// ── form state ────────────────────────────────────────────────────────────────
const form = ref({ name: '', email: '', password: '', password_confirmation: '' })
const errors = ref({})
const saving = ref(false)
const success = ref('')
const failure = ref('')
const showPwd  = ref(false)

// ── image ─────────────────────────────────────────────────────────────────────
const fileRef     = ref(null)
const previewUrl  = ref(null)
const pickedFile  = ref(null)

const avatarSrc = computed(() => {
    if (previewUrl.value) return previewUrl.value
    const p = authStore.user?.profile_image
    if (p) return p.startsWith('http') ? p : '/storage/' + p
    const n = encodeURIComponent(form.value.name || 'U')
    return 'https://ui-avatars.com/api/?name=' + n + '&background=10b981&color=fff&size=160'
})

const pickImage = () => fileRef.value?.click()

const onFile = (e) => {
    const f = e.target.files?.[0]
    if (!f) return
    pickedFile.value = f
    previewUrl.value = URL.createObjectURL(f)
}

// ── init ──────────────────────────────────────────────────────────────────────
onMounted(() => {
    const u = authStore.user || {}
    form.value.name  = u.name  || ''
    form.value.email = u.email || ''
})

// ── helpers ───────────────────────────────────────────────────────────────────
const fieldErr = (key) => errors.value[key] || ''

// ── save ──────────────────────────────────────────────────────────────────────
const save = async () => {
    saving.value  = true
    errors.value  = {}
    success.value = ''
    failure.value = ''

    try {
        let payload

        if (pickedFile.value) {
            const fd = new FormData()
            fd.append('name',  form.value.name  || '')
            fd.append('email', form.value.email || '')
            if (form.value.password) {
                fd.append('password', form.value.password)
                fd.append('password_confirmation', form.value.password_confirmation)
            }
            fd.append('profile_image', pickedFile.value)
            payload = fd
        } else {
            payload = {
                name:  form.value.name,
                email: form.value.email,
                ...(form.value.password
                    ? { password: form.value.password, password_confirmation: form.value.password_confirmation }
                    : {}),
            }
        }

        const res = await updateProfile(payload)

        if (res.data.code === 'SUCCESS') {
            authStore.setUser(res.data.user)
            form.value.password              = ''
            form.value.password_confirmation = ''
            pickedFile.value  = null
            previewUrl.value  = null
            showPwd.value     = false
            success.value = 'Profile updated successfully.'
            setTimeout(() => { success.value = '' }, 4000)
        }
    } catch (err) {
        if (err.response?.status === 422) {
            const raw = err.response.data?.errors || {}
            const mapped = {}
            for (const k in raw) mapped[k] = raw[k][0]
            errors.value = mapped
        } else {
            failure.value = 'Something went wrong. Please try again.'
        }
    } finally {
        saving.value = false
    }
}
</script>
